import {
  buttonVariants,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@e2n/ui';

import { cn } from '@/lib/utils';
import {
  faChevronLeft,
  faChevronRight,
  faImage,
  faNewspaper,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { useProfile } from '@/hooks';
import { useNews } from '@/domain/navigation/features/navigation/hooks/useNews/useNews';
import { useUpdateNewsStatus } from '@/domain/navigation/features/navigation/hooks/useUpdateNewsStatus/useUpdateNewsStatus';

// eslint-disable-next-line max-lines-per-function
export function NewsfeedDialog() {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [updateNewsStatus] = useUpdateNewsStatus();
  const profile = useProfile();

  const { id } = useParams();
  const { news } = useNews();
  const currentNewsItem = news.find((item) => item.id === id);

  function onHandleClose() {
    navigate(location.state?.background ?? location, {
      replace: true,
    });
  }

  function getURLToNextNewsItemById() {
    const currentIndex = news.findIndex((item) => item.id === id);
    if (currentIndex === -1 || currentIndex === news.length - 1) {
      return `/news/${news[0].id}`;
    }
    return `/news/${news[currentIndex + 1].id}`;
  }

  function getURLToPreviousNewsItemById() {
    const currentIndex = news.findIndex((item) => item.id === id);
    if (currentIndex <= 0) {
      return `/news/${news[news.length - 1].id}`;
    }
    return `/news/${news[currentIndex - 1].id}`;
  }

  function markNextNewsItemAsRead() {
    const currentIndex = news.findIndex((item) => item.id === id);
    if (currentIndex === -1 || currentIndex === news.length - 1) {
      updateNewsStatus({ newsItemId: news[0].id, istGelesen: true }).catch(
        (error) => {
          Sentry.captureException(`Could not update news status: ${error}`, {
            tags: { email: profile.manager?.email },
          });
        },
      );
    } else {
      updateNewsStatus({
        newsItemId: news[currentIndex + 1].id,
        istGelesen: true,
      }).catch((error) => {
        Sentry.captureException(`Could not update news status: ${error}`, {
          tags: { email: profile.manager?.email },
        });
      });
    }
  }

  function markPreviousNewsItemAsRead() {
    const currentIndex = news.findIndex((item) => item.id === id);
    if (currentIndex <= 0) {
      updateNewsStatus({
        newsItemId: news[news.length - 1].id,
        istGelesen: true,
      }).catch((error) => {
        Sentry.captureException(`Could not update news status: ${error}`, {
          tags: { email: profile.manager?.email },
        });
      });
    } else {
      updateNewsStatus({
        newsItemId: news[currentIndex - 1].id,
        istGelesen: true,
      }).catch((error) => {
        Sentry.captureException(`Could not update news status: ${error}`, {
          tags: { email: profile.manager?.email },
        });
      });
    }
  }

  return (
    <Dialog open={true} onOpenChange={onHandleClose}>
      <DialogContent className="w-screen h-screen border-none top-0 translate-y-0 p-8 transition-all duration-300 overflow-y-auto md:border md:top-[10%] md:max-w-170 md:max-h-[80dvh] md:h-fit">
        <DialogHeader className="hidden md:flex">
          <div className="flex items-center gap-2 text-neutrals-900">
            <FontAwesomeIcon icon={faNewspaper} />
            <DialogTitle className="text-sm">
              {t('general.navigation.profileDropdown.news')}
            </DialogTitle>
          </div>
        </DialogHeader>
        <div className="flex flex-col gap-6 mt-6 md:mt-0">
          <div className="bg-neutrals-300 rounded-md relative flex justify-center items-center">
            {!currentNewsItem?.bildUrl ? (
              <div className="flex flex-col items-center gap-3 text-neutrals-800 h-32 md:h-45 justify-center">
                <FontAwesomeIcon icon={faImage} size="2x" />
                <div>{t('general.navigation.newsfeed.noImage')}</div>
              </div>
            ) : (
              <img
                src={currentNewsItem.bildUrl}
                alt={currentNewsItem.titel}
                className="w-full h-full md:h-45 rounded-md"
              />
            )}
            <div className="absolute top-3 right-3 flex items-center gap-2">
              <Link
                onClick={markPreviousNewsItemAsRead}
                className={cn(
                  buttonVariants({ variant: 'circle', size: 'circle' }),
                )}
                to={getURLToPreviousNewsItemById()}
                state={{
                  background: location.state?.background ?? location,
                }}>
                <FontAwesomeIcon icon={faChevronLeft} />
              </Link>
              <Link
                onClick={markNextNewsItemAsRead}
                className={cn(
                  buttonVariants({ variant: 'circle', size: 'circle' }),
                )}
                to={getURLToNextNewsItemById()}
                state={{
                  background: location.state?.background ?? location,
                }}>
                <FontAwesomeIcon icon={faChevronRight} />
              </Link>
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <div className="text-xl font-bold">{currentNewsItem?.titel}</div>
            <div className="text-xs text-neutrals-700">
              {currentNewsItem?.veroeffentlichtAmRelative()}
            </div>
          </div>
          {/* 
              Eigentlich sollte man hier nicht direkt HTML Code über dangerouslySetInnerHTML reinreichen. 
              Da wir aber den Newsfeed selber schreiben und die Kontrolle darüber haben, welcher Inhalt reinkommt, ist das mMn eine valide Lösung
          */}
          <div
            className="unreset text-sm text-neutrals-900"
            dangerouslySetInnerHTML={{ __html: currentNewsItem?.inhalt ?? '' }}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
